import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Re-reads the session cookie's user. Used on mount and again after the Auth0
  // popup flow establishes a session out-of-band (see hooks/useAuthPopup).
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/users/me');
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        return response.data;
      }
      return null;
    } catch (error) {
      // A 401 error is expected if the user is not logged in
      if (error.response && error.response.status !== 401) {
        console.error('Failed to fetch user session:', error);
      }
      setUser(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check for an existing session when the app loads
    refreshUser();
  }, [refreshUser]);

  const login = async (email, password) => {
    try {
      // The backend uses 'username', so we'll map email to it.
      const response = await api.post('/auth/login', { username: email, password });
      if (response.data.success) {
        // After successful login, fetch the user's full profile
        const userResponse = await api.get('/users/me');
        setUser(userResponse.data);
        setIsAuthenticated(true);
        navigate('/profile'); // Redirect to profile page on successful login
      }
      return response.data;
    } catch (error) {
      console.error('Login failed:', error.response?.data?.message || error.message);
      throw error.response?.data || new Error('An unknown error occurred');
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      setIsAuthenticated(false);
      navigate('/'); // Redirect to homepage on logout
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
    setUser, // Expose setUser to allow components to update user state (e.g., after a purchase)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};